/**
 * Plan-aware step helpers for the sequence builder (card P1). The caps
 * themselves live in @dealcloser/shared (sequence-cap.ts), which the
 * campaigns worker enforces on save.
 */
import { followUpNumber } from '@dealcloser/shared'
import type { SequenceStep, SequenceStepType } from './types'

/** The next follow-up a new step should be: one past the highest so far, within the plan's cap. */
export function nextFollowUpType(steps: readonly SequenceStep[], maxFollowUps: number): SequenceStepType {
  const highest = Math.max(0, ...steps.map((s) => followUpNumber(s.type) ?? 0))
  return `follow_up_${Math.max(1, Math.min(highest + 1, maxFollowUps))}` as SequenceStepType
}

/** True when the plan may use this step type (follow-ups numbered past the cap are not offered). */
export function stepTypeAllowed(type: SequenceStepType, maxFollowUps: number): boolean {
  return (followUpNumber(type) ?? 0) <= maxFollowUps
}

const MAX_STEP_SAMPLES = 3

const nonEmpty = (samples: readonly string[] | undefined): string[] =>
  (samples ?? []).filter((s) => s.trim().length > 0)

/**
 * The samples of a campaign still on the retired "One set for all steps"
 * (style_samples_mode 'global', or unset, which the workers read as global).
 * [] when it is per step or has none.
 */
export function legacyGlobalSamples(
  sequence: { readonly style_samples_mode?: string; readonly style_samples_global?: readonly string[] },
): string[] {
  if (sequence.style_samples_mode === 'per_step') return []
  return nonEmpty(sequence.style_samples_global).slice(0, MAX_STEP_SAMPLES)
}

/**
 * "Move these onto each step" (plan N4): global mode gives the samples to
 * every step at send time, so moving them copies them onto every step. The
 * global samples go first, then any samples a step already had, without
 * duplicates, up to the 3 a step holds. Copying them only to the First DM
 * would silently take them off every follow-up.
 */
export function moveGlobalSamplesToEveryStep(
  steps: readonly SequenceStep[],
  samples: readonly string[],
): SequenceStep[] {
  return steps.map((step) => {
    const merged = [...new Set([...nonEmpty(samples), ...nonEmpty(step.style_samples)])].slice(0, MAX_STEP_SAMPLES)
    return { ...step, style_samples: merged }
  })
}
